import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, RefreshCw, Building2, Settings, Upload, LayoutDashboard, FolderOpen, Mail, FileSearch, BookOpen, Sparkles, Users } from 'lucide-react';
import { InfoModal } from '../common/InfoModal';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, apiPatch } from '../../utils/api';
import toast from 'react-hot-toast';

// Tab components
import CommandCenterTab from './tabs/CommandCenterTab';
import ContactHubTab from './tabs/ContactHubTab';
import DirectorDashboardTab from './tabs/DirectorDashboardTab';
import ReviewPipelineTab from './tabs/ReviewPipelineTab';
import AuditFilterBar from './AuditFilterBar';
import AuditPropertyTable from './AuditPropertyTable';
import PropertySidePanel from './PropertySidePanel';
import AuditManualEntryForm from './AuditManualEntryForm';
import AuditSettingsPanel from './AuditSettingsPanel';
import AuditImportModal from './AuditImportModal';
import ReviewPipelineUploadModal from './ReviewPipelineUploadModal';
import Modal from '../common/modals/Modal';
import PortfolioExplorer from './portfolio/PortfolioExplorer';
import AskJPro from './AskJPro';

const AuditTrackerDashboard = () => {
  const { theme } = useTheme();
  const { currentUser, canAccess } = useAuth();
  const d = theme === 'dark';
  const { clientSlug, auditId: urlAuditId } = useParams();

  // Active tab
  // Directors/admins land on their portfolio-wide overview; everyone else on the
  // Portfolio front door.
  const isDirectorOrAdmin = currentUser?.admin === true || currentUser?.role === 'director';
  const [activeTab, setActiveTab] = useState(() => (isDirectorOrAdmin ? 'director' : 'portfolio'));

  // Data state
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [audits, setAudits] = useState([]);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Properties tab state
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [auditRole, setAuditRole] = useState(null); // { role, see_all, can_write, can_admin }
  const [filters, setFilters] = useState({ search: '', status: '', stage: '', assigned_to_id: '', audit_month: '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showUploadModal, setShowStatusReportImport] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [showManageClients, setShowManageClients] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showAskJPro, setShowAskJPro] = useState(false);
  const [clientPortfolioData, setClientPortfolioData] = useState(null);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      const response = await apiGet('/admin/audit_tracker/clients');
      if (!response.ok) throw new Error('Failed to load clients');
      const data = await response.json();
      setClients(data.clients || []);
      if (data.clients?.length > 0) {
        setSelectedClientId((prev) => {
          if (prev && data.clients.find((c) => c.id === prev)) return prev;
          if (clientSlug) {
            const matched = data.clients.find((c) => c.slug === clientSlug);
            if (matched) return matched.id;
          }
          return data.clients[0].id;
        });
      }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [clientSlug]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Load audits when client/year changes
  useEffect(() => {
    if (!selectedClientId) return;
    const loadAudits = async () => {
      try {
        const response = await apiGet(`/admin/audit_tracker/clients/${selectedClientId}/audits?year=${selectedYear}`);
        if (!response.ok) return;
        const data = await response.json();
        setAudits(data.audits || []);
        if (data.audits?.length > 0) {
          if (urlAuditId) {
            const matched = data.audits.find((a) => String(a.id) === urlAuditId);
            setSelectedAuditId(matched ? matched.id : data.audits[0].id);
          } else {
            setSelectedAuditId(data.audits[0].id);
          }
        } else {
          setSelectedAuditId(null);
          setProperties([]);
        }
      } catch {}
    };
    loadAudits();

    // Load team members
    apiGet(`/admin/audit_tracker/clients/${selectedClientId}`).then(async (r) => {
      if (r.ok) { const d = await r.json(); setTeamMembers(d.members || []); }
    }).catch(() => {});

    // Clear cached portfolio data when client changes
    setClientPortfolioData(null);
  }, [selectedClientId, selectedYear]);

  // Load properties
  const loadProperties = useCallback(async () => {
    if (!selectedAuditId) return;
    setLoadingProperties(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '20' });
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.stage) params.set('stage', filters.stage);
      if (filters.assigned_to_id && filters.assigned_to_id !== 'unassigned') params.set('assigned_to_id', filters.assigned_to_id);
      if (filters.audit_month) params.set('audit_month', filters.audit_month);
      if (filters.quick) params.set('quick', filters.quick);
      if (filters.state) params.set('state', filters.state);
      if (filters.processor_company) params.set('processor_company', filters.processor_company);
      if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
      if (filters.has_files) params.set('has_files', filters.has_files);
      if (filters.min_files_required) params.set('min_files_required', filters.min_files_required);
      const response = await apiGet(`/admin/audit_tracker/audits/${selectedAuditId}/properties?${params}`);
      if (!response.ok) return;
      const data = await response.json();
      setProperties(data.properties || []);
      setPagination(data.pagination || null);
      if (data.audit_role) setAuditRole(data.audit_role);
    } catch {}
    finally { setLoadingProperties(false); }
  }, [selectedAuditId, page, filters]);

  useEffect(() => { if (activeTab === 'properties') loadProperties(); }, [loadProperties, activeTab]);

  const handleTransition = async (propertyId, newStatus) => {
    try {
      const response = await apiPatch(`/admin/audit_tracker/properties/${propertyId}/transition`, { status: newStatus });
      if (!response.ok) { const d = await response.json(); throw new Error(d.error); }
      toast.success(`Status changed to ${newStatus.replace(/_/g, ' ')}`);
      loadProperties();
    } catch (err) { toast.error(err.message); }
  };

  const handleCreateAudit = async () => {
    if (!selectedClientId) return;
    try {
      const response = await apiPost(`/admin/audit_tracker/clients/${selectedClientId}/audits`, { year: selectedYear });
      if (!response.ok) { const d = await response.json(); throw new Error(d.error); }
      const data = await response.json();
      toast.success(data.message);
      setAudits((prev) => [data.audit, ...prev]);
      setSelectedAuditId(data.audit.id);
    } catch (err) { toast.error(err.message); }
  };

  // Front-door Portfolio: picking/provisioning a company there activates its
  // client and drops into the work tabs.
  const handleOpenClient = async (clientId, year) => {
    if (!clientId) return;
    await loadClients();
    if (year) setSelectedYear(year); // match the year used in the Portfolio tab
    setSelectedClientId(clientId);
    setPage(1);
    setActiveTab('command');
  };

  const handleFilterChange = (newFilters) => { setFilters(newFilters); setPage(1); };

  const handleAskJPro = async () => {
    if (!selectedClientId) return;
    setShowAskJPro(true);
    if (clientPortfolioData?.client_id === selectedClientId) return; // already loaded for this client
    try {
      const res = await apiGet(`/admin/audit_tracker/clients/${selectedClientId}/portfolio_summary`);
      if (res.ok) {
        const data = await res.json();
        setClientPortfolioData({ ...data, client_id: selectedClientId });
      }
    } catch {}
  };

  // Navigate from Command Center to Properties with a filter. The funnel now
  // emits mirror stages (no_cycle/not_started/collecting/in_review/complete);
  // "attention" maps to the behind-the-cycle quick filter.
  const handleFilterToStatus = (stage) => {
    setActiveTab('properties');
    setFilters((f) => (stage === 'attention'
      ? { ...f, status: '', stage: '', quick: 'attention' }
      : { ...f, status: '', quick: '', stage }));
    setPage(1);
  };

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  const selectClass = `px-3 py-2 rounded-lg border text-sm font-rasa font-medium ${
    d ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
  } focus:outline-none focus:ring-2 focus:ring-blue-500/50`;

  const tabClass = (tab) => `flex items-center gap-2 px-4 py-2.5 text-[13px] font-rasa font-medium transition-colors border-b-2 ${
    activeTab === tab
      ? 'text-blue-500 border-blue-500'
      : `${d ? 'text-gray-400 border-transparent hover:text-gray-200' : 'text-gray-500 border-transparent hover:text-gray-700'}`
  }`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className={`p-6 ${d ? 'text-gray-100' : 'text-gray-800'}`}>

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-dm-serif">Audit Tracker</h1>
          <p className={`text-[13px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage LIHTC audit lifecycle
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedClient && (
            <span className={`px-3 py-2 rounded-lg text-sm font-rasa font-medium ${d ? 'bg-gray-800 text-gray-200' : 'bg-blue-50 text-blue-700'}`}>
              {selectedClient.name}
            </span>
          )}
          <select value={selectedYear} onChange={(e) => { setSelectedYear(Number(e.target.value)); setPage(1); }} className={selectClass}>
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleAskJPro} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-rasa font-medium bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 shadow-sm" title="Ask JPro about this client">
            <Sparkles size={14} /> Ask JPro
          </button>
          <button onClick={() => setShowGuide(true)} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-rasa font-medium ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`} title="Feature Guide">
            <BookOpen size={14} /> Guide
          </button>
          <button onClick={() => { if (activeTab === 'properties') loadProperties(); }} className={`p-2 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setShowManageClients(true)} className={`p-2 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Settings">
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* ── Settings overlay ── */}
      {showManageClients && (
        <AuditSettingsPanel onClose={() => setShowManageClients(false)} onClientChanged={loadClients} />
      )}

      {/* ── Tabs + content (front-door Portfolio drives the active company) ── */}
      {!showManageClients && (
        <>
          {/* Tab Bar — always visible; Portfolio is the front door */}
          <div className={`flex border-b mb-6 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            {(!auditRole || auditRole.can_admin) && (
              <button className={tabClass('director')} onClick={() => setActiveTab('director')}>
                <LayoutDashboard size={15} /> Director
              </button>
            )}
            <button className={tabClass('portfolio')} onClick={() => setActiveTab('portfolio')}>
              <Building2 size={15} /> Portfolio
            </button>
            <button className={tabClass('command')} onClick={() => setActiveTab('command')}>
              <LayoutDashboard size={15} /> Command Center
            </button>
            <button className={tabClass('properties')} onClick={() => setActiveTab('properties')}>
              <FolderOpen size={15} /> Properties
            </button>
            <button className={tabClass('contacts')} onClick={() => setActiveTab('contacts')}>
              <Users size={15} /> Contacts
            </button>
            <button className={tabClass('review')} onClick={() => setActiveTab('review')}>
              <FileSearch size={15} /> Review Pipeline
            </button>
          </div>

          {/* Work tabs need an active company+audit (chosen in Portfolio) */}
          {['command', 'properties', 'contacts', 'review'].includes(activeTab) && !selectedAuditId && (
            <div className={`text-center py-16 rounded-xl border ${d ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <Building2 size={40} className={`mx-auto mb-4 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
              {selectedClientId ? (
                <>
                  <p className={`text-lg font-dm-serif mb-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>No {selectedYear} audit for {selectedClient?.name}</p>
                  <button onClick={handleCreateAudit} className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-rasa">Create {selectedYear} Audit</button>
                </>
              ) : (
                <p className={`text-lg font-dm-serif mb-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>Pick a company in the Portfolio tab to start.</p>
              )}
            </div>
          )}

          {/* ── Tab: Command Center ── */}
          {selectedAuditId && activeTab === 'command' && (
            <CommandCenterTab
              auditId={selectedAuditId}
              auditYear={selectedYear}
              onPropertyClick={(id) => { setActiveTab('properties'); setSelectedPropertyId(id); }}
              onFilterToStatus={handleFilterToStatus}
            />
          )}

          {/* ── Tab: Properties ── */}
          {selectedAuditId && activeTab === 'properties' && (
            <div className="space-y-3">
              {/* Filter bar */}
              <AuditFilterBar filters={filters} onFilterChange={handleFilterChange} teamMembers={teamMembers} />

              {/* Active stage/quick filter chip (set from the Command Center funnel; not in the bar's dropdowns) */}
              {(filters.stage || filters.quick === 'attention') && (
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-rasa font-medium ${d ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    {filters.quick === 'attention'
                      ? 'Needs attention'
                      : `Stage: ${filters.stage.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}`}
                    <button
                      onClick={() => { setFilters((f) => ({ ...f, stage: '', quick: f.quick === 'attention' ? '' : f.quick })); setPage(1); }}
                      className="hover:opacity-70"
                      aria-label="Clear filter"
                    >×</button>
                  </span>
                </div>
              )}

              {/* Actions row — hidden for viewers */}
              {(!auditRole || auditRole.can_write) && (
                <div className="flex items-center gap-2 justify-end">
                  {(!auditRole || auditRole.can_admin) && (
                    <>
                      <button onClick={() => { setEditProperty(null); setShowManualEntry(true); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[12px] font-rasa font-medium whitespace-nowrap">
                        <Plus size={13} /> Add Property
                      </button>
                      <button onClick={() => setShowImport(true)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-rasa font-medium whitespace-nowrap ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                        <Upload size={13} /> Import Portfolio
                      </button>
                      <button onClick={() => setShowStatusReportImport(true)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-rasa font-medium whitespace-nowrap ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                        <Upload size={13} /> Upload Files
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Table */}
              <div className={`rounded-xl border ${d ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'} shadow-sm`}>
                {loadingProperties ? (
                  <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>
                ) : (
                  <AuditPropertyTable
                    properties={properties}
                    pagination={pagination}
                    onPageChange={setPage}
                    onPropertyClick={(prop) => setSelectedPropertyId(prop.id)}
                    onTransition={handleTransition}
                    teamMembers={teamMembers}
                    onBulkAssign={async (propertyIds, assignToId) => {
                      try {
                        const res = await apiPost(`/admin/audit_tracker/audits/${selectedAuditId}/bulk_assign`, {
                          property_ids: propertyIds,
                          assigned_to_id: assignToId,
                        });
                        if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                        const data = await res.json();
                        toast.success(data.message);
                        loadProperties();
                      } catch (err) { toast.error(err.message); }
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Tab: Outreach ── */}
          {selectedAuditId && activeTab === 'contacts' && <ContactHubTab auditId={selectedAuditId} />}

          {/* ── Tab: Review Pipeline ── */}
          {selectedAuditId && activeTab === 'review' && <ReviewPipelineTab auditId={selectedAuditId} />}

          {/* ── Tab: Portfolio Explorer (front door — pick a company to work on) ── */}
          {activeTab === 'portfolio' && <PortfolioExplorer onOpenClient={handleOpenClient} />}

          {/* ── Tab: Processor Sync (admin) ── */}
          {activeTab === 'director' && <DirectorDashboardTab />}
        </>
      )}

      {/* ── Side Panel (Properties tab) or Modal (other tabs) ── */}
      {selectedPropertyId && (
        <PropertySidePanel
          propertyId={selectedPropertyId}
          onClose={() => setSelectedPropertyId(null)}
          teamMembers={teamMembers}
          onPropertyUpdated={() => loadProperties()}
        />
      )}
      <AuditManualEntryForm
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        auditId={selectedAuditId}
        editProperty={editProperty}
        onSaved={() => loadProperties()}
      />
      <AuditImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        auditId={selectedAuditId}
        onImported={() => loadProperties()}
      />
      <ReviewPipelineUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowStatusReportImport(false)}
        auditId={selectedAuditId}
        existingProperties={properties}
        onImported={() => loadProperties()}
      />

      {/* Feature Guide Modal */}
      <InfoModal isOpen={showGuide} onClose={() => setShowGuide(false)} title="Audit Tracker Guide">
        <div className="space-y-4 text-sm">
          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-blue-500" />
              Command Center
            </h4>
            <p className="mb-2">Your morning standup view — see portfolio health at a glance.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>KPI Cards</strong> — Portfolio progress, collection rate, attention needed, avg completion time</li>
              <li><strong>Audit Pipeline</strong> — Visual lifecycle flow. Click any segment to filter to that status</li>
              <li><strong>Needs Action</strong> — Top urgent properties ranked by urgency. Click to open details</li>
              <li><strong>Monthly Progress</strong> — Completed vs expected trajectory chart</li>
              <li><strong>Team Workload</strong> — Who's handling what, by phase</li>
              <li><strong>Ask JPro</strong> — AI assistant for portfolio questions</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-blue-500" />
              Properties Tab
            </h4>
            <p className="mb-2">Manage individual properties — filter, assign, take actions.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Smart Filter</strong> — Type natural language: "properties in Georgia with no contact"</li>
              <li><strong>Quick Filters</strong> — One-click: No Contact, No Units, Unassigned, Overdue, Attention</li>
              <li><strong>Bulk Assign</strong> — Check multiple properties → assign to a team member</li>
              <li><strong>Side Panel</strong> — Click any property to see details, contacts, files, timeline</li>
              <li><strong>Import</strong> — Upload the Portfolio Tracker workbook to bulk-update all properties</li>
              <li><strong>File Selection</strong> — Upload a rent roll → AI selects 20% of tenants for audit</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-500" />
              Outreach Tab
            </h4>
            <p className="mb-2">Track all email communications and follow-up effectiveness.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Email Analytics</strong> — Total sent, response rate, follow-up counts by tier</li>
              <li><strong>Scheduled Queue</strong> — Pending follow-ups with Send Now / Cancel</li>
              <li><strong>Email Log</strong> — Searchable history of every email sent</li>
              <li><strong>Templates</strong> — Create custom email templates per escalation tier</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <FileSearch className="w-4 h-4 text-blue-500" />
              Review Pipeline Tab
            </h4>
            <p className="mb-2">Track tenant file reviews, corrections, and approvals.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Summary Cards</strong> — Total files, pending review, corrections, approved</li>
              <li><strong>Corrections Tracker</strong> — Files needing corrections, sorted by urgency</li>
              <li><strong>Property File Table</strong> — Expandable rows showing each tenant file's status</li>
              <li><strong>Upload Reports</strong> — Upload per-property status reports (.xls) to update file data</li>
              <li><strong>File Selection</strong> — Run 20% selection directly from a property row</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4 text-blue-500" />
              Settings (Gear Icon)
            </h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Clients</strong> — Add/manage audit clients, create new clients</li>
              <li><strong>Team</strong> — Add team members with roles (Lead, Auditor, Viewer)</li>
              <li><strong>Permissions</strong> — Configure what each role can see and do</li>
              <li><strong>Email</strong> — Set default sender, follow-up schedule (business days per tier), templates</li>
              <li><strong>Dashboard</strong> — Control what the client sees (hide columns, rename labels)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">The Audit Flow</h4>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800 text-xs space-y-1">
              <p>1. <strong>Import</strong> Portfolio Tracker workbook → properties seeded with contacts and schedules</p>
              <p>2. <strong>Setup Email</strong> sent to property → requests rent roll and utility allowance</p>
              <p>3. <strong>Rent Roll Received</strong> → run AI 20% file selection → send file request to property</p>
              <p>4. <strong>Follow-ups</strong> fire automatically every 5 business days until files arrive</p>
              <p>5. <strong>Files Received</strong> → follow-ups cancel → property moves to review</p>
              <p>6. <strong>Review & Corrections</strong> → tracked per tenant file with correction rounds</p>
              <p>7. <strong>All Files Approved</strong> → final report uploaded → property marked complete</p>
              <p>8. <strong>Year Rollover</strong> → completed properties roll to next audit year</p>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Keyboard Tips</h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li>Use the <strong>Smart Filter</strong> to search with natural language instead of clicking dropdowns</li>
              <li><strong>Ask JPro</strong> for instant analysis — "What are the biggest bottlenecks?"</li>
              <li><strong>Click pipeline segments</strong> in Command Center to jump to filtered properties</li>
            </ul>
          </section>
        </div>
      </InfoModal>

      {/* ── Ask JPro (Client Portfolio) Modal ── */}
      {showAskJPro && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/40" onClick={() => setShowAskJPro(false)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <AskJPro
              portfolioData={clientPortfolioData || {}}
              mode="client-portfolio"
              audience="internal"
              onClose={() => setShowAskJPro(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrackerDashboard;
